import { Trans, useTranslation } from 'next-i18next'
import Link from 'next/link'
import { useRouter } from 'next/router'
import type { ComponentProps } from 'react'
import { buttonVariants } from '~/components/ui/button'
import EventCard from '~/domains/events/components/event-card/event-card'
import { useFeaturedEvents } from '~/domains/events/hooks/use-featured-events.hook'
import { ExtraMissingMenus } from '~/domains/extras/components/atoms/missing-menus'
import { cn } from '~/lib/utils'
import { useTracking } from '~/providers/tracking-provider'
import { useUserContext } from '~/providers/user-provider'

export const FeaturedEventSection = ({ className }: ComponentProps<'div'>) => {
	const { t } = useTranslation()
	const { track } = useTracking()
	const { userQuery } = useUserContext()
	const { route } = useRouter()

	const {
		event,
		daysBeforeEvent,
		daysBeforeEventLabel,
		emptyMenusQty,
		isExtrasUpdatable,
		extrasExpirationDate,
	} = useFeaturedEvents()

	const isDashboard = route === '/dashboard'
	const shouldShowMenuMsg =
		!!emptyMenusQty && isExtrasUpdatable && !!extrasExpirationDate

	const i18nValues = {
		daysBeforeEvent: t(daysBeforeEventLabel, { count: daysBeforeEvent }),
		first_name: !!userQuery.data?.first_name
			? ` ${userQuery.data?.first_name}`
			: null,
	}

	if (!event) return null

	return (
		<div
			className={cn('flex flex-col gap-y-8 md:flex-row md:gap-x-8', className)}
		>
			<div className='col-span-12 flex flex-1 flex-col gap-y-4 lg:col-span-4'>
				{isDashboard ? (
					<Trans
						parent='p'
						i18nKey='myEvents:featuredEvent.titleGreetings'
						values={i18nValues}
						className='font-bold text-4xl'
					/>
				) : (
					<Trans
						parent='p'
						i18nKey='myEvents:featuredEvent.title'
						values={i18nValues}
						className='text-xl'
					/>
				)}

				{shouldShowMenuMsg && (
					<div className='flex flex-col gap-y-4'>
						<ExtraMissingMenus
							count={emptyMenusQty}
							date={extrasExpirationDate}
						/>

						<div>
							<Link
								className={buttonVariants({ variant: 'outline' })}
								href={`event/${event.id}?activeTab=extras`}
								onClick={() => track.click('featuredEvent-chooseMenu-button')}
							>
								{t('myEvents:featuredEvent.choseMenuBtn')}
							</Link>
						</div>
					</div>
				)}
			</div>

			<EventCard
				event={event}
				isFeatured
			/>
		</div>
	)
}

export default FeaturedEventSection
